import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import CodeMirror from '@uiw/react-codemirror'
import { ISelectOption } from 'components/forms/Selects'
import { IconButton } from 'transfix-ui/components/'
import { ConfirmationModal } from 'transfix-ui/components/Modals'
import { TSelectOpt } from 'transfix-ui/components/Select/SelectTypes'
import { SingleSelect } from 'transfix-ui/components/SingleSelect'
import { Box, Button, makeStyles, Typography } from 'transfix-ui/core'
import {
  IBuilderLibraries,
  IStupidEdiConfigs,
  ITransactionSetWithSubtype,
  Maybe,
} from 'types/graphqlTypes'
import { AddIcon, EditIcon, SettingsIcon } from 'transfix-icons'
import { useModal } from 'utils/hooks'
import { tfx12Config } from '../../EdiSelfService/types'
import { EDI_TOOLS_ROUTES } from '../../LandingPage'
import { useConfigurableBuilderVersion } from '../hooks/useConfigurableBuilderVersion'
import { IConfigurableBuilderVersionSchema } from '../schema/ConfigurableBuilderVersionSchema'
import { ConfigurableBuilderVersionModal } from './ConfigurableBuilderVersionModal'

interface IEditBuilderContainerProps {
  displayUserFeedback: (message: string, level?: 'error' | 'info') => void
  extractionTemplate: string
  extractionTemplateChangeHandler: (value: string) => void
  builderLibrary: IBuilderLibraries
  builderLibraryChangeHandler: (value: IBuilderLibraries) => void
  stupidediConfig: Maybe<IStupidEdiConfigs>
  stupidediConfigChangeHandler: (value: Maybe<IStupidEdiConfigs>) => void
  stupidediConfigOptions: string[]
  tfx12DefinitionId: Maybe<string>
  tfx12DefinitionIdChangeHandler: (value: Maybe<string>) => void
  tfx12ConfigOptions: tfx12Config[]
  runQueryHandler: (
    extractionTemplate: string,
    builderLibrary: IBuilderLibraries,
    stupidediConfig: Maybe<IStupidEdiConfigs>,
    tfx12DefinitionId: Maybe<string>
  ) => void
  settingsClickHandler: () => void
  selectedBuilderId: string
  outboundConfigId: string
  builderVersionId: string | null
  canBuildBalanceDuesChangeHandler: (value: boolean) => void
  canBuildBalanceDues: boolean
  transactionSetWithSubtype: ITransactionSetWithSubtype
}

const useStyles = makeStyles(theme => ({
  editor: {
    borderRight: '1px solid',
    '& .cm-content': {
      border: '1px solid',
      borderRight: 'none',
      fontFamily: 'monospace',
    },
    '& .cm-lineNumbers': {
      minWidth: 33,
    },
    '& .cm-line::selection': {
      backgroundColor: '#d9d9d9 !important',
    },
  },
  column: {
    flex: 5,
  },
  columnLarge: {
    flex: 7,
  },
  runQueryButton: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
  versionSelect: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
}))

export const EditBuilderContainer = ({
  builderLibrary,
  builderLibraryChangeHandler,
  builderVersionId,
  canBuildBalanceDuesChangeHandler,
  displayUserFeedback,
  extractionTemplate,
  extractionTemplateChangeHandler,
  outboundConfigId,
  runQueryHandler,
  selectedBuilderId,
  settingsClickHandler,
  stupidediConfig,
  stupidediConfigChangeHandler,
  stupidediConfigOptions,
  tfx12ConfigOptions,
  tfx12DefinitionId,
  tfx12DefinitionIdChangeHandler,
  transactionSetWithSubtype,
}: IEditBuilderContainerProps) => {
  const history = useHistory()
  const classes = useStyles()
  const {
    activateConfigurableBuilderVersion,
    configurableBuilderVersions,
    createConfigurableBuilderVersion,
    isLoading: isLoadingVersions,
    selectedConfigurableBuilderVersion,
    setSelectedConfigurableBuilderVersion,
    updateConfigurableBuilderVersion,
  } = useConfigurableBuilderVersion({
    builderId: selectedBuilderId,
    displayUserFeedback,
    builderLibraryChangeHandler,
    stupidediConfigChangeHandler,
    tfx12DefinitionIdChangeHandler,
    canBuildBalanceDuesChangeHandler,
  })

  const versionSelectOptions: TSelectOpt[] = useMemo(() => {
    return [...configurableBuilderVersions]
      .sort((a, b) => {
        return parseInt(a.id, 10) - parseInt(b.id, 10)
      })
      .map(version => ({
        value: version.id,
        label: `${version.id} ${version.isLive ? '(live)' : ''}`,
      }))
  }, [configurableBuilderVersions])

  const { closeModal, isOpen, openModal } = useModal()

  const [persistedExtractionTemplate, setPersistedExtractionTemplate] = useState<string>('')
  const [extractionTemplateIsDirty, setExtractionTemplateIsDirty] = useState<boolean>(false)

  useEffect(() => {
    setExtractionTemplateIsDirty(persistedExtractionTemplate !== extractionTemplate)
  }, [extractionTemplate, persistedExtractionTemplate])

  const loadBuilderVersion = useCallback(
    (version: IConfigurableBuilderVersionSchema | undefined) => {
      if (version) {
        setSelectedConfigurableBuilderVersion(version)
        extractionTemplateChangeHandler(version.extractionTemplate)
        setPersistedExtractionTemplate(version.extractionTemplate)
        builderLibraryChangeHandler(version.builderLibrary)
        stupidediConfigChangeHandler(version.stupidediConfig)
        tfx12DefinitionIdChangeHandler(version.tfx12DefinitionId)
        canBuildBalanceDuesChangeHandler(version.canBuildBalanceDues)
      } else {
        setSelectedConfigurableBuilderVersion(null)
      }
    },
    [
      builderLibraryChangeHandler,
      extractionTemplateChangeHandler,
      setSelectedConfigurableBuilderVersion,
      stupidediConfigChangeHandler,
      tfx12DefinitionIdChangeHandler,
      canBuildBalanceDuesChangeHandler,
    ]
  )

  useEffect(() => {
    if (builderVersionId) {
      const version = configurableBuilderVersions.find(version => version.id === builderVersionId)
      loadBuilderVersion(version)
    }
  }, [builderVersionId, configurableBuilderVersions, loadBuilderVersion])

  const onChangeVersion = (data?: ISelectOption) => {
    const { value } = data || {}
    const selectedVersion = configurableBuilderVersions.find(version => version.id === value)
    if (selectedVersion) {
      history.push(
        `${EDI_TOOLS_ROUTES.SELF_SERVE_OUTBOUND}/builder/${selectedBuilderId}/builder-version/${selectedVersion?.id}`
      )
    }
  }

  // TODO: if selectedBuilderId and !selectedConfigurableBuilderVersion
  // automatically choose the 'live' version. (if any versions exist.)

  const getVersionSelectContainer = () => {
    if (isLoadingVersions) {
      return <Typography variant='subtitle2'>Loading versions...</Typography>
    }
    if (versionSelectOptions.length === 0) {
      return <Typography variant='subtitle2'>No existing versions</Typography>
    }
    return (
      <Box marginRight={1}>
        <SingleSelect
          className={classes.versionSelect}
          id='selectVersion'
          label='Builder Version'
          options={versionSelectOptions}
          onSelect={onChangeVersion}
          selected={selectedConfigurableBuilderVersion?.id}
          dataTest='builder-version-select'
          clearable={false}
        />
        {selectedConfigurableBuilderVersion && (
          <IconButton
            Icon={EditIcon}
            color='secondary'
            onClick={openModal('editVersionModal')}
            size='large'
          />
        )}
      </Box>
    )
  }

  const lookupTfx12ConfigId = (tfx12ConfigReference: Maybe<string>) => {
    if (tfx12ConfigOptions) {
      const tfx12Config = tfx12ConfigOptions.find(
        config => config.reference === tfx12ConfigReference
      )
      return tfx12Config?.id
    }
    return null
  }

  const handleCreateVersion = (data: IConfigurableBuilderVersionSchema) => {
    data.tfx12DefinitionId = lookupTfx12ConfigId(data.tfx12DefinitionId)
    createConfigurableBuilderVersion(data)
  }

  const handleEditVersion = (data: IConfigurableBuilderVersionSchema) => {
    data.tfx12DefinitionId = lookupTfx12ConfigId(data.tfx12DefinitionId)
    updateConfigurableBuilderVersion(data)
  }

  const getDefaultVersionValues = () => {
    if (selectedConfigurableBuilderVersion) {
      return {
        ...selectedConfigurableBuilderVersion,
        extractionTemplate,
      } as IConfigurableBuilderVersionSchema
    }
    return {
      ediMapperConfigurableBuilderId: selectedBuilderId,
      builderLibrary: IBuilderLibraries.STUPIDEDI,
      stupidediConfig: IStupidEdiConfigs.DEFAULT,
      tfx12DefinitionId: '',
      extractionTemplate,
      canBuildBalanceDues: false,
    } as IConfigurableBuilderVersionSchema
  }

  const runBuilderHandler = () => {
    runQueryHandler(extractionTemplate, builderLibrary, stupidediConfig, tfx12DefinitionId)
  }

  const saveDirtyExtractionTemplate = (data: IConfigurableBuilderVersionSchema | null) => {
    if (data) {
      data.extractionTemplate = extractionTemplate
      handleEditVersion(data)
    }
  }

  const dirtyExtractionTemplateHandler = () => {
    saveDirtyExtractionTemplate(selectedConfigurableBuilderVersion)
  }

  const activateButtonClickHandler = openModal('activateVersionModal')

  return (
    <Box className={`${classes.column} ${classes.columnLarge}`} marginX={2}>
      {isOpen('addVersionModal') && (
        <ConfigurableBuilderVersionModal
          isEdit={false}
          onCancel={closeModal}
          onClose={closeModal}
          version={getDefaultVersionValues()}
          onSubmit={handleCreateVersion}
          stupidediConfigOptions={stupidediConfigOptions}
          tfx12ConfigOptions={tfx12ConfigOptions}
          transactionSetWithSubtype={transactionSetWithSubtype}
        />
      )}
      {isOpen('editVersionModal') && (
        <ConfigurableBuilderVersionModal
          isEdit
          onCancel={closeModal}
          onClose={closeModal}
          version={getDefaultVersionValues()}
          onSubmit={handleEditVersion}
          stupidediConfigOptions={stupidediConfigOptions}
          tfx12ConfigOptions={tfx12ConfigOptions}
          transactionSetWithSubtype={transactionSetWithSubtype}
        />
      )}
      {selectedConfigurableBuilderVersion?.id && (
        <ConfirmationModal
          title={`Make version ${selectedConfigurableBuilderVersion?.id} active?`}
          size={3}
          open={isOpen('activateVersionModal')}
          onClose={closeModal}
          closeOnBackdropClick
          actions={[
            {
              label: 'Cancel',
              type: 'dismiss',
            },
            {
              label: 'Confirm',
              type: 'confirm',
              action: () => {
                activateConfigurableBuilderVersion(
                  selectedConfigurableBuilderVersion.id,
                  outboundConfigId
                )
                closeModal()
              },
            },
          ]}
        />
      )}
      <Box
        marginY={1}
        display='flex'
        flexDirection='row'
        flexWrap='wrap'
        justifyContent='space-between'>
        <Typography variant='h3'>Enter your query here:</Typography>
        <Box display='flex' alignItems='center'>
          {selectedConfigurableBuilderVersion && !selectedConfigurableBuilderVersion.isLive && (
            <Button
              disabled={isLoadingVersions}
              onClick={
                extractionTemplateIsDirty
                  ? dirtyExtractionTemplateHandler
                  : activateButtonClickHandler
              }>
              {extractionTemplateIsDirty ? 'Save' : 'Activate'}
            </Button>
          )}
          {getVersionSelectContainer()}
          <IconButton
            Icon={AddIcon}
            color='secondary'
            onClick={openModal('addVersionModal')}
            size='large'
          />
          <Button className={classes.runQueryButton} onClick={runBuilderHandler}>
            Run Builder
          </Button>
          <IconButton
            Icon={SettingsIcon}
            color='secondary'
            onClick={settingsClickHandler}
            size='large'
          />
        </Box>
      </Box>
      <CodeMirror
        height='40vh'
        maxWidth='70vw'
        className={classes.editor}
        value={extractionTemplate}
        onChange={extractionTemplateChangeHandler}
      />
    </Box>
  )
}
